import { useMemo } from 'react';

import { AlertCircleIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { useColorContrast } from 'hooks/color-contrast-checker';
import { useTranslation } from 'react-i18next';

export const ColorContrastCard = ({ foreground, background, hideDescription = false }) => {
  const { t } = useTranslation();
  const { ratio, compliance, contrastData } = useColorContrast(foreground, background);

  const getBackground = useMemo(() => {
    if (ratio >= 7) return 'surface-success-light';
    if (ratio >= 4.5 && ratio < 7) return 'surface-warning-light';
    return 'surface-error-light';
  }, [ratio]);

  const getTextColor = useMemo(() => {
    if (ratio >= 7) return 'text-success';
    if (ratio >= 4.5 && ratio < 7) return 'text-warning';
    return 'text-error';
  }, [ratio]);

  const getColor = useMemo(() => {
    if (ratio >= 7) return '#02722f';
    if (ratio >= 4.5 && ratio < 7) return '#8a6d3b';
    return '#c32d2d';
  }, [ratio]);

  return (
    <div className={cn('flex justify-start gap-4 w-full', compliance.AA && 'items-center')}>
      <div
        className={cn(
          'u-padding u-gap-medium u-border-radius--x-small surface-white d-flex align-items-center justify-content-center u-width-40',
          getBackground,
        )}>
        <span className={cn('u-font-size-xx-large u-font-weight-800', getTextColor)}>{ratio}</span>

        <div className="d-flex flex-column align-items-center u-gap-small">
          <span className={cn('u-font-size-small u-font-weight-600', getTextColor)}>
            {contrastData.text}
          </span>
          <div className="d-flex u-gap-xx-small">
            {[...Array(5)].map((_, index) => (
              <FeatherIcon
                key={index}
                icon="star"
                fill={index < contrastData.rating ? getColor : '#ffffff90'}
                stroke={getTextColor}
                size="16"
              />
            ))}
          </div>
        </div>
      </div>

      <div className="d-flex flex-column u-gap flex-grow-1">
        <div className="d-flex u-gap-small flex-column">
          <span className="primary-text u-font-size-medium u-font-weight-500">
            {t('common.textContrastCheckTitle')}
          </span>
          <span className="secondary-text u-font-size-x-small">
            {!hideDescription && t('common.textContrastCheckDescription')}
            <a
              className="u-link dark"
              target="_blank"
              href="https://www.w3.org/TR/WCAG22/#contrast-minimum">
              WCAG 2.1 accessibility standards
            </a>
          </span>
        </div>

        {!compliance.AA && (
          <div className="u-padding--small u-border u-border-radius--x-small surface-white d-flex align-items-center">
            <HugeiconsIcon
              size="24"
              icon={AlertCircleIcon}
              strokeWidth="1.5"
              className="text-error"
            />{' '}
            <span className="secondary-text u-font-size-x-small u-margin-left--small">
              {t('common.textContrastCheckWarning')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
